import { TextInput } from 'components/input/TextInput/TextInput';
import type { InputProps } from 'hooks/useInput/useInput';
import React from 'react';

import type { Principle } from './PrinciplesList/PrinciplesList';
import { PrinciplesList } from './PrinciplesList/PrinciplesList';

import './PasswordInput.scss';

const MIN_LENGTH_REGEXP = /^.{8,}/;
const DIGIT_REGEXP = /\d+/;
const UPPER_AND_LOWER_CASE_REGEXP = /(?=.*[a-z])(?=.*[A-Z])/;
const SPECIAL_CHARS_REGEXP = /[!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]/;
// biome-ignore lint/suspicious/noExplicitAny: false positive
type AnyFunction = (...args: any[]) => void;

const callAll =
  <T extends AnyFunction[]>(...fns: T) =>
  (...args: Parameters<T[number]>) => {
    fns.forEach(fn => fn && fn(...args));
  };

const PRINCIPLES: Principle[] = [
  {
    key: 'password.principles.length',
    predicate: (password: string) => password.match(MIN_LENGTH_REGEXP),
  },
  {
    key: 'password.principles.upper_and_lower_case',
    predicate: (password: string) =>
      password.match(UPPER_AND_LOWER_CASE_REGEXP),
  },
  {
    key: 'password.principles.special_chars',
    predicate: (password: string) => password.match(SPECIAL_CHARS_REGEXP),
  },
  {
    key: 'password.principles.digit',
    predicate: (password: string) => password.match(DIGIT_REGEXP),
  },
];

type PasswordInputProps = Omit<InputProps, 'value'> & {
  principles?: Principle[];
  value?: string;
};

function PasswordInput({
  principles = PRINCIPLES,
  onChange,
  error,
  ...rest
}: PasswordInputProps) {
  const [password, setPassword] = React.useState<string | undefined>();
  const [isValid, setIsValid] = React.useState(true);

  return (
    <div className="PasswordInput">
      <TextInput
        {...rest}
        aria-describedby="password-hints"
        type="password"
        onChange={callAll(onChange, (_name: string, value: string) =>
          setPassword(value),
        )}
        error={error || (password && !isValid)}
        data-testid="password"
      />
      <PrinciplesList
        className="pt-4"
        password={password || ''}
        principles={principles}
        onChange={({ result }) => setIsValid(result)}
      />
    </div>
  );
}

export { PasswordInput, PRINCIPLES };
